// User.ts - 保留部门信息的用户模型
import SafeUtil from '../../util/SafeUtil';
import BaseEntity from '../base/BaseEntity';
import { UserRole } from './UserRole';
import { UserStatus, UserStatusList } from './UserStatus';
import ImageUtil from '../../util/ImageUtil';
import Lang from '../global/Lang';
import defaultAvatarPath from '../../../assets/image/avatar.png';
import Filter from '../base/filter/Filter';
import SortFilter from '../base/filter/SortFilter';
import InputFilter from '../base/filter/InputFilter';
import SelectionFilter from '../base/filter/SelectionFilter';
import Space from '../space/Space';
import CryptoUtil from '../../util/CryptoUtil';
import MessageBoxUtil from '../../util/MessageBoxUtil';

// 定义允许登录的设备类型
export enum AllowedDeviceType {
  WEB = 'WEB',
  PCAPP = 'PCAPP',
  MOBILE = 'MOBILE'
}

export const AllowedDeviceTypeList = [
  { value: AllowedDeviceType.WEB, name: Lang.t('user.webClient', '网页端') },
  { value: AllowedDeviceType.PCAPP, name: Lang.t('user.pcClient', 'PC端') },
  { value: AllowedDeviceType.MOBILE, name: Lang.t('user.mobileClient', '移动端') }
];

// 设备信息接口
export interface DeviceInfo {
  client_ip?: string;
  client_mac?: string;
  disk_serial?: string;
  device_name?: string;
  os_info?: string;
  login_time?: string;
  source?: string;
}

// 加密参数接口
interface EncryptedLoginParams {
  encryptedData: string;
  salt: string;
  device?: AllowedDeviceType;
  [key: string]: any;
}

// 部门信息接口
export interface DeptInfo {
  id?: string;
  name?: string;
  shortName?: string;
  type?: string;
}

// 用户限制类型枚举
export enum UserLimitType {
  READ_WRITE = 'READ_WRITE',      // 默认权限，读写权限
  UPLOAD_PREVIEW = 'UPLOAD_PREVIEW', // 上传预览权限
  READ_ONLY = 'READ_ONLY'         // 只读权限
}

// 用户限制类型选项列表
export const UserLimitTypeList = [
  { 
    value: UserLimitType.READ_WRITE, 
    name: Lang.t('space.memberRoleReadWrite', '读写权限'),
    description: Lang.t('user.readWriteDesc', '可以上传、预览、下载和删除文件')
  },
  { 
    value: UserLimitType.UPLOAD_PREVIEW, 
    name: Lang.t('user.uploadPreview', '上传预览权限'),
    description: Lang.t('user.uploadPreviewDesc', '可以上传和预览文件，但不能下载')
  },
  { 
    value: UserLimitType.READ_ONLY, 
    name: Lang.t('user.readOnly', '只读权限'),
    description: Lang.t('user.readOnlyDesc', '只能下载和预览文件，不能上传和删除改名等')
  }
];

export default class User extends BaseEntity {
  // 获取当前登录者的信息
  static URL_INFO = '/api/user/info';
  static URL_LOGIN = '/api/user/login';
  static URL_AUTHENTICATION_LOGIN = '/api/user/authentication/login';
  static URL_REGISTER = '/api/user/register';
  static URL_LOGOUT = '/api/user/logout';
  static URL_USER_CHANGE_PASSWORD = '/api/user/change/password';
  static URL_USER_RESET_PASSWORD = '/api/user/reset/password';
  static URL_USER_TOGGLE_STATUS = '/api/user/toggle/status';
  static URL_USER_TRANSFIGURATION = '/api/user/transfiguration';
  static URL_USER_SCAN = '/api/user/scan';
  
  // 用户同步相关的 URL
  static URL_USER_SYNC_TRIGGER = '/api/user/sync/trigger';
  static URL_USER_SYNC_TRIGGER_SELECTED = '/api/user/sync/trigger-selected';

  role: UserRole = UserRole.GUEST;
  username: string | null = null;
  password: string | null = null;
  avatarUrl: string | null = null;
  lastIp: string | null = null;
  lastTime: Date = new Date();
  status: UserStatus = UserStatus.OK;
  spaceUuid: string | null = null;
  space: Space = new Space();
  allowedDevices: AllowedDeviceType[] = [AllowedDeviceType.WEB, AllowedDeviceType.PCAPP];
  allowedIpRange: string | null = null;
  webAllowedIpRange: string | null = null;
  pcAllowedIpRange: string | null = null;
  
  // 拆分为两个独立的特征绑定选项
  bindWebFeature: boolean = false;
  bindPcFeature: boolean = false;
  
  disableShare: boolean = false;
  disablePublicShare: boolean = false;
  
  // 部门相关字段 - 保留这些字段
  deptId: string | null = null;
  deptName: string | null = null;
  deptShortName: string | null = null;
  deptInfo: DeptInfo | null = null;
  
  // 用户限制类型字段
  userLimitType: UserLimitType = UserLimitType.READ_WRITE;

  // 授权截止日期（null 表示永久用户）
  expirationDate: Date | null = null;

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  assign(obj: any) {
    super.assign(obj);
  
    this.assignEntity('lastTime', Date);
    this.assignEntity('space', Space);
    
    // 处理allowedDevices数组
    if (obj.allowedDevices) {
      this.allowedDevices = Array.isArray(obj.allowedDevices) 
        ? obj.allowedDevices 
        : [obj.allowedDevices];
    } else {
      this.allowedDevices = [AllowedDeviceType.WEB, AllowedDeviceType.PCAPP];
    }

    // 处理旧的allowedIpRange字段（向后兼容）
    if (obj.allowedIpRange !== undefined) {
      this.allowedIpRange = obj.allowedIpRange;
    }

    // 处理新的分端IP范围字段
    if (obj.webAllowedIpRange !== undefined) {
      this.webAllowedIpRange = obj.webAllowedIpRange;
    }
    
    if (obj.pcAllowedIpRange !== undefined) {
      this.pcAllowedIpRange = obj.pcAllowedIpRange;
    }

    // 兼容性处理：如果新的IP范围字段为空，但旧字段有值，则使用旧字段的值
    if (!this.webAllowedIpRange && this.allowedIpRange) {
      this.webAllowedIpRange = this.allowedIpRange;
    }
    
    if (!this.pcAllowedIpRange && this.allowedIpRange) {
      this.pcAllowedIpRange = this.allowedIpRange;
    }

    // 处理bindWebFeature（Web端特征绑定）
    if (obj.bindWebFeature !== undefined) {
      this.bindWebFeature = Boolean(obj.bindWebFeature);
    } else if (obj.bindDeviceFeature !== undefined) {
      // 向后兼容：如果旧字段存在，将其赋值给Web端特征
      this.bindWebFeature = Boolean(obj.bindDeviceFeature);
    } else {
      this.bindWebFeature = false;
    }

    // 处理bindPcFeature（PC端特征绑定）
    if (obj.bindPcFeature !== undefined) {
      this.bindPcFeature = Boolean(obj.bindPcFeature);
    } else if (obj.bindDeviceFeature !== undefined) {
      // 向后兼容：如果旧字段存在，将其赋值给PC端特征
      this.bindPcFeature = Boolean(obj.bindDeviceFeature);
    } else {
      this.bindPcFeature = false;
    }

    // 处理disableShare
    if (obj.disableShare !== undefined) {
      this.disableShare = Boolean(obj.disableShare);
    } else {
      this.disableShare = false;
    }

    // 处理disablePublicShare
    if (obj.disablePublicShare !== undefined) {
      this.disablePublicShare = Boolean(obj.disablePublicShare);
    } else {
      this.disablePublicShare = false;
    }
    
    // 处理部门信息 - 保留部门相关字段
    if (obj.deptId !== undefined) {
      this.deptId = obj.deptId;
    }
    
    if (obj.deptName !== undefined) {
      this.deptName = obj.deptName;
    }
    
    if (obj.deptShortName !== undefined) {
      this.deptShortName = obj.deptShortName;
    }
    
    if (obj.deptInfo !== undefined) {
      this.deptInfo = obj.deptInfo;
    }
    
    // 兼容旧数据：如果deptInfo存在但其他字段为空，则填充
    if (this.deptInfo && !this.deptId && this.deptInfo.id) {
      this.deptId = this.deptInfo.id;
    }
    if (this.deptInfo && !this.deptName && this.deptInfo.name) {
      this.deptName = this.deptInfo.name;
    }
    if (this.deptInfo && !this.deptShortName && this.deptInfo.shortName) {
      this.deptShortName = this.deptInfo.shortName;
    }
    
    // 处理用户限制类型
    if (obj.userLimitType !== undefined) {
      this.userLimitType = obj.userLimitType as UserLimitType;
    } else {
      this.userLimitType = UserLimitType.READ_WRITE; // 默认值
    }

    // 处理授权截止日期
    if (obj.expirationDate !== undefined && obj.expirationDate !== null) {
      this.assignEntity('expirationDate', Date);
    } else {
      this.expirationDate = null;
    }
  }

  hasLogin(): boolean {
    return this.role !== UserRole.GUEST;
  }

  isAdmin(): boolean {
    return this.role === UserRole.ADMINISTRATOR;
  }

  getTAG(): string {
    return 'user';
  }

  getFilters(): Filter[] {
    return [
      ...super.getFilters(),
      new SortFilter(Lang.t('user.lastLoginTime', '按上次登录时间排序'), 'orderLastTime'),
      new InputFilter(Lang.t('user.username'), 'username', Lang.t('user.usernamesearch', '按用户名搜索')),
      new SelectionFilter(Lang.t('user.status', '状态'), 'status', UserStatusList),
      // 部门筛选 - 保留部门相关筛选
      new InputFilter(Lang.t('department.shortName', '部门简称'), 'deptShortName', Lang.t('department.shortNamePlaceholder', '请输入部门简称')),
    ];
  }

  getForm(): any {
    return {
      username: this.username,
      password: this.password,
      role: this.role,
      avatarUrl: this.avatarUrl,
      uuid: this.uuid ? this.uuid : null,
      allowedDevices: this.allowedDevices,
      allowedIpRange: this.allowedIpRange,
      webAllowedIpRange: this.webAllowedIpRange,
      pcAllowedIpRange: this.pcAllowedIpRange,
      bindWebFeature: this.bindWebFeature,
      bindPcFeature: this.bindPcFeature,
      disableShare: this.disableShare,
      disablePublicShare: this.disablePublicShare,
      // 部门字段 - 保留
      deptId: this.deptId,
      deptName: this.deptName,
      deptShortName: this.deptShortName,
      // 用户限制类型字段
      userLimitType: this.userLimitType,
      // 授权截止日期
      expirationDate: this.expirationDate,
    };
  }

  getAvatarUrl(origin: boolean = false) {
    if (this.avatarUrl) {
      return ImageUtil.handleImageUrl(this.avatarUrl, origin);
    } else {
      return defaultAvatarPath;
    }
  }

  // 获取部门显示名称（优先显示简称）- 保留这个方法
  getDeptDisplayName(): string {
    if (this.deptShortName) {
      return this.deptShortName;
    }
    if (this.deptName) {
      return this.deptName;
    }
    return '--';
  }

  // 获取用户限制类型显示名称
  getUserLimitTypeDisplayName(): string {
    const limitType = UserLimitTypeList.find(item => item.value === this.userLimitType);
    return limitType ? limitType.name : Lang.t('user.readWriteDesc', '读写权限');
  }

  // 获取用户限制类型描述
  getUserLimitTypeDescription(): string {
    const limitType = UserLimitTypeList.find(item => item.value === this.userLimitType);
    return limitType ? limitType.description : '';
  }

  // 检查是否允许特定设备登录
  isDeviceAllowed(deviceType: AllowedDeviceType): boolean {
    return this.allowedDevices.includes(deviceType);
  }

  // 检查IP是否在允许范围内
  isIpAllowed(ip: string, deviceType?: AllowedDeviceType): boolean {
    // 根据设备类型选择相应的IP范围
    let ipRange: string | null = null;
    
    if (deviceType === AllowedDeviceType.WEB && this.webAllowedIpRange) {
      ipRange = this.webAllowedIpRange;
    } else if (deviceType === AllowedDeviceType.PCAPP && this.pcAllowedIpRange) {
      ipRange = this.pcAllowedIpRange;
    } else if (this.allowedIpRange) {
      ipRange = this.allowedIpRange;
    }
    
    // 如果没有设置IP范围，则允许所有IP
    if (!ipRange) {
      return true;
    }
    
    return this.checkIpInRange(ip, ipRange);
  }
  
  // 检查IP是否在指定范围内
  private checkIpInRange(ip: string, ipRange: string): boolean {
    if (!ipRange || !ip) {
      return true;
    }
    
    const allowedRanges = ipRange.split(',').map(range => range.trim());
    
    for (const range of allowedRanges) {
      if (range === '') continue;
      
      // 如果是CIDR格式
      if (range.includes('/')) {
        if (this.isIpInCidr(ip, range)) {
          return true;
        }
      } 
      // 如果是单个IP地址
      else if (this.isValidIp(ip) && this.isValidIp(range)) {
        if (ip === range) {
          return true;
        }
      }
      // 如果是IP段（如192.168.1.）
      else if (range.endsWith('.')) {
        if (ip.startsWith(range)) {
          return true;
        }
      }
    }
    
    return false;
  }
  
  // 检查IP地址格式
  private isValidIp(ip: string): boolean {
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipPattern.test(ip)) {
      return false;
    }
    
    const parts = ip.split('.');
    for (const part of parts) {
      const num = parseInt(part, 10);
      if (num < 0 || num > 255) {
        return false;
      }
    }
    
    return true;
  }
  
  // 检查IP是否在CIDR范围内
  private isIpInCidr(ip: string, cidr: string): boolean {
    try {
      const [subnet, prefixStr] = cidr.split('/');
      const prefix = parseInt(prefixStr, 10);
      
      if (!this.isValidIp(ip) || !this.isValidIp(subnet) || isNaN(prefix) || prefix < 0 || prefix > 32) {
        return false;
      }
      
      const ipNum = this.ipToNumber(ip);
      const subnetNum = this.ipToNumber(subnet);
      const mask = (~((1 << (32 - prefix)) - 1)) >>> 0;
      
      return (ipNum & mask) === (subnetNum & mask);
    } catch (error) {
      console.error('CIDR检查错误:', error);
      return false;
    }
  }
  
  // IP地址转数字
  private ipToNumber(ip: string): number {
    const parts = ip.split('.');
    let result = 0;
    for (let i = 0; i < 4; i++) {
      result = result * 256 + parseInt(parts[i], 10);
    }
    return result;
  }

  // 检查登录权限
  canLogin(deviceType: AllowedDeviceType, clientIp: string): boolean {
    // 检查设备类型是否允许
    if (!this.isDeviceAllowed(deviceType)) {
      return false;
    }
    
    // 检查IP是否允许
    if (!this.isIpAllowed(clientIp, deviceType)) {
      return false;
    }
    
    // 检查授权是否过期
    if (this.isExpired()) {
      return false;
    }
    
    // 检查特征绑定
    if (deviceType === AllowedDeviceType.WEB && this.bindWebFeature) {
      // 这里可以添加Web端特征绑定的检查逻辑
    }
    
    if (deviceType === AllowedDeviceType.PCAPP && this.bindPcFeature) {
      // 这里可以添加PC端特征绑定的检查逻辑
    }
    
    return true;
  }

  // 获取设备对应的IP范围
  getDeviceIpRange(deviceType: AllowedDeviceType): string | null {
    if (deviceType === AllowedDeviceType.WEB) {
      return this.webAllowedIpRange || this.allowedIpRange;
    } else if (deviceType === AllowedDeviceType.PCAPP) {
      return this.pcAllowedIpRange || this.allowedIpRange;
    }
    return this.allowedIpRange;
  }

  /**
   * 加密登录参数
   */
  private encryptLoginParams(params: { username: string; password: string }): EncryptedLoginParams {
    try {
      const loginData = {
        username: params.username,
        password: params.password,
        timestamp: Date.now()
      };
      
      const jsonString = JSON.stringify(loginData);
      const { encrypted, salt } = CryptoUtil.encryptWithSalt(jsonString);
      
      return {
        encryptedData: encrypted,
        salt: salt
      };
    } catch (error) {
      console.error('加密登录参数失败:', error);
      throw new Error(Lang.t('error.operationFailed', '操作失败'));
    }
  }

  /**
   * 加密普通参数
   */
  private encryptParams(params: any): { encryptedData: string; salt: string } {
    try {
      const jsonString = JSON.stringify(params);
      const { encrypted, salt } = CryptoUtil.encryptWithSalt(jsonString);
      return {
        encryptedData: encrypted,
        salt: salt
      };
    } catch (error) {
      console.error('加密参数失败:', error);
      throw new Error(Lang.t('error.operationFailed', '操作失败'));
    }
  }

  httpLoginWithDeviceInfo(
    params: {
      username: string;
      password: string;
      device?: AllowedDeviceType;
    } & DeviceInfo,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    
    const { username, password, device = AllowedDeviceType.WEB, ...deviceInfo } = params;
    
    let encryptedParams: EncryptedLoginParams;
    try {
      encryptedParams = this.encryptLoginParams({ username, password });
    } catch (error) {
      SafeUtil.safeCallback(errorCallback)(error);
      return;
    }
    
    let form: any = {
      ...encryptedParams,
      device,
      ...deviceInfo
    };
  
    this.httpPost(
      User.URL_LOGIN,
      form,
      function (response: any) {
        that.assign(response.data.data);
        
        // 确保用户UUID被正确设置
        console.log(Lang.t('user.loginSuccess', '登录成功') + '，用户UUID:', that.uuid);
        
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  // 使用FormData的登录方法
  httpLoginWithFormData(
    formData: FormData,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
  
    this.httpPost(
      User.URL_LOGIN,
      formData,
      function (response: any) {
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  // 注册
  httpRegister(
    username: string,
    password: string,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;

    // 加密用户名和密码
    let encryptedParams: { encryptedData: string; salt: string };
    try {
      encryptedParams = this.encryptParams({ 
        username, 
        password,
        timestamp: Date.now()
      });
    } catch (error) {
      SafeUtil.safeCallback(errorCallback)(error);
      return;
    }

    this.httpPost(
      User.URL_REGISTER,
      encryptedParams,
      function (response: any) {
        that.assign(response.data.data);

        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  // 获取当前登录者的信息
  httpInfo(loginRequired: boolean, finalCallback?: any) {
    let that = this;
    if (loginRequired) {
      // httpGet有统一的登录处理机制
      this.httpPost(
        User.URL_INFO,
        {},
        function (response: any) {
          that.assign(response.data.data);
        },
        null,
        finalCallback
      );
    } else {
      this.httpPureGet(
        User.URL_INFO,
        {},
        function (response: any) {
          that.assign(response.data.data);
        },
        () => {},
        finalCallback
      );
    }
  }

  httpSave(
    spaceInfo: {
      sizeLimit: number;
      totalSizeLimit: number;
      userLimitType?: UserLimitType;
    },
    deviceInfo?: {
      allowedDevices?: AllowedDeviceType[];
      webAllowedIpRange?: string;
      pcAllowedIpRange?: string;
      bindWebFeature?: boolean;
      bindPcFeature?: boolean;
      disableShare?: boolean;
      disablePublicShare?: boolean;
    },
    successCallback?: any,
    errorCallback?: any,
    finallyCallback?: any
  ) {
    let that = this;
  
    let url = this.getUrlCreate();
    if (this.uuid) {
      url = this.getUrlEdit();
    }
  
    this.errorMessage = this.validate();
    if (this.errorMessage) {
      that.defaultErrorHandler(this.errorMessage, errorCallback);
      return;
    }
  
    // 构建请求数据 - 包含部门信息和用户限制类型
    const requestData = { 
      ...this.getForm(), 
      ...spaceInfo,
      ...(deviceInfo || {})
    };
  
    // 确保 allowedDevices 是数组格式
    if (requestData.allowedDevices && !Array.isArray(requestData.allowedDevices)) {
      requestData.allowedDevices = [requestData.allowedDevices];
    }
    
    // 向后兼容处理：如果新的IP范围字段有值，旧的alloweIpRange应该被清空
    if (requestData.webAllowedIpRange || requestData.pcAllowedIpRange) {
      // 清除旧的统一IP范围，因为现在使用分端的IP范围
      requestData.allowedIpRange = null;
    } else if (requestData.allowedIpRange) {
      // 如果使用了旧的IP范围，将其应用到两个新的字段
      requestData.webAllowedIpRange = requestData.allowedIpRange;
      requestData.pcAllowedIpRange = requestData.allowedIpRange;
    }
  
    this.httpPost(
      url,
      requestData,
      function (response: any) {
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finallyCallback
    );
  }

  // 退出登录
  httpLogout(successCallback?: any, errorCallback?: any, finalCallback?: any) {
    let that = this;

    let form = {};

    this.httpPost(
      User.URL_LOGOUT,
      form,
      function (response: any) {
        console.info(Lang.t('user.logout', '退出成功！'));

        that.assign(new User());

        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback,
      finalCallback
    );
  }

  httpChangePassword(
    oldPassword: string,
    newPassword: string,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    
    // 加密密码参数
    let encryptedParams: { encryptedData: string; salt: string };
    try {
      encryptedParams = this.encryptParams({
        oldPassword,
        newPassword,
        timestamp: Date.now()
      });
    } catch (error) {
      SafeUtil.safeCallback(errorCallback)(error);
      return;
    }

    this.httpPost(
      User.URL_USER_CHANGE_PASSWORD,
      encryptedParams,
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }

  httpResetPassword(
    password: string,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    // 加密密码参数
    let encryptedParams: { encryptedData: string; salt: string };
    try {
      encryptedParams = this.encryptParams({
        userUuid: this.uuid,
        password: password,
        timestamp: Date.now()
      });
    } catch (error) {
      SafeUtil.safeCallback(errorCallback)(error);
      return;
    }

    this.httpPost(
      User.URL_USER_RESET_PASSWORD,
      encryptedParams,
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }

  httpToggleStatus(
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    this.httpPost(
      User.URL_USER_TOGGLE_STATUS,
      { uuid: this.uuid },
      function (response: any) {
        that.assign(response.data.data);
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }

  httpAuthenticationLogin(
    authentication: string,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    
    // 加密认证参数
    let encryptedParams: { encryptedData: string; salt: string };
    try {
      encryptedParams = this.encryptParams({
        authentication,
        timestamp: Date.now()
      });
    } catch (error) {
      SafeUtil.safeCallback(errorCallback)(error);
      return;
    }

    this.httpPost(
      User.URL_AUTHENTICATION_LOGIN,
      encryptedParams,
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response);
      },
      errorCallback
    );
  }

  httpTransfiguration(
    successCallback: (text: string) => void,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    let form = { uuid: this.uuid };
    this.httpPost(
      User.URL_USER_TRANSFIGURATION,
      form,
      function (response: any) {
        successCallback(response.data.msg);
      },
      errorCallback
    );
  }

  httpLoginWithParams(
    params: {
      username: string;
      password: string;
      device?: AllowedDeviceType;
    } & DeviceInfo,
    successCallback?: any,
    errorCallback?: any,
    finalCallback?: any
  ) {
    // 直接调用现有的 httpLoginWithDeviceInfo 方法
    return this.httpLoginWithDeviceInfo(params, successCallback, errorCallback, finalCallback);
  }

  httpScan(
    successCallback: (text: string) => void,
    errorCallback?: any,
    finalCallback?: any
  ) {
    let that = this;
    let form = { uuid: this.uuid };
    this.httpPost(
      User.URL_USER_SCAN,
      form,
      function (response: any) {
        successCallback(response.data.msg);
      },
      errorCallback,
      finalCallback
    );
  }

  /**
   * 错误通知方法
   */
  notifyError(error: any) {
    let errorMessage = Lang.t('error.operationFailed', '操作失败');
    
    if (error && error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && error.data && error.data.message) {
      errorMessage = error.data.message;
    }
    
    MessageBoxUtil.error(errorMessage);
  }

  /**
   * 触发用户同步到远程服务器
   */
  httpTriggerSync(
    targetServer: string,
    successCallback?: any,
    errorCallback?: any
  ) {
    let that = this;

    console.log(Lang.t('user.sync', '发送同步请求到:'), User.URL_USER_SYNC_TRIGGER);
    console.log(Lang.t('user.targetServer', '目标服务器:'), targetServer);

    this.httpPost(
      User.URL_USER_SYNC_TRIGGER,
      { targetServer: targetServer },
      function (response: any) {
        console.log(Lang.t('common.operationSuccess', '操作成功'), response);
        SafeUtil.safeCallback(successCallback)(response.data);
      },
      function (error: any) {
        console.error(Lang.t('common.operationFailed', '操作失败'), error);
        that.notifyError(error);
        SafeUtil.safeCallback(errorCallback)(error);
      }
    );
  }

  /**
   * 触发选中用户的同步到远程服务器
   */
  httpTriggerSyncSelected(
    targetServer: string,
    usernames: string[],
    successCallback?: any,
    errorCallback?: any
  ) {
    let that = this;

    this.httpPost(
      User.URL_USER_SYNC_TRIGGER_SELECTED,
      { 
        targetServer: targetServer,
        usernames: JSON.stringify(usernames)
      },
      function (response: any) {
        SafeUtil.safeCallback(successCallback)(response.data);
      },
      function (error: any) {
        that.notifyError(error);
        SafeUtil.safeCallback(errorCallback)(error);
      }
    );
  }

  // ==================== 授权截止日期相关方法 ====================

  /**
   * 判断是否为永久用户（未设置截止日期）
   */
  isPermanent(): boolean {
    return this.expirationDate === null;
  }

  /**
   * 判断授权是否已过期
   * @returns true 表示已过期且非永久用户；false 表示未过期或永久用户
   */
  isExpired(): boolean {
    if (this.isPermanent()) {
      return false;
    }
    if (!this.expirationDate) {
      return false;
    }
    const now = new Date();
    return this.expirationDate < now;
  }

  /**
   * 获取授权剩余天数（永久用户返回 -1）
   * @returns 剩余天数，-1 表示永久用户，0 表示今日到期，负数表示已过期
   */
  getRemainingDays(): number {
    if (this.isPermanent()) {
      return -1;
    }
    if (!this.expirationDate) {
      return -1;
    }
    const now = new Date();
    // 重置时间为当日0点以便计算日期差
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const expireDay = new Date(this.expirationDate.getFullYear(), this.expirationDate.getMonth(), this.expirationDate.getDate());
    const diffTime = expireDay.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * 获取授权截止日期的格式化字符串（本地日期格式）
   */
  getFormattedExpirationDate(): string {
    if (this.isPermanent()) {
      return Lang.t('user.permanent', '永久');
    }
    if (!this.expirationDate) {
      return '--';
    }
    return this.expirationDate.toLocaleDateString();
  }


}